import {
  createEffect,
  createResource,
  createSignal,
  For,
  Match,
  onCleanup,
  Show,
  Switch,
} from "solid-js";
import {
  assetCardIcon,
  assetUpiApps,
  assetUpiIcon,
  upiLogo,
} from "../../assets/assets";
import { OrderDetails, RedemptionResponse } from "../../server/types/order";
import {
  getStoreOrderDetails,
  initPgRedemption,
  payOrderStandardCheckout,
} from "../../data/products";
import {
  goBack,
  HubbleRoute,
  popUntilHome,
  pushPage,
} from "../../shared_states/modal";
import {
  showSnackBar,
  snackApiCallWrapper,
} from "../../shared_states/snackbar";
import { classNames } from "../../utils/etc";
import { createJob, JobWrapper } from "../../utils/job";
import { toRupees } from "../../utils/number";
import { openRazorpayCheckout } from "../../utils/third_party/razorpay";
import { ButtonRegular, ThreeDotLoader } from "../../widgets/button";
import { PhosphorIcon } from "../../widgets/icons";
import { RedemptionTransactions } from "../transactions/transactions";
import { VoucherCart } from "../voucher_edit/voucher";
import { VoucherGenSuccess } from "./voucher_gen_success";
import { config } from "../../data/config";
import { sdkEventManager, HubbleEvent } from "../../data/events";
import {
  DiscountDetails,
  SimulatedTransactionDetails,
} from "~/server/types/brand";
import { PaymentLoader } from "./payment_loader";
import { useSearchParams } from "@solidjs/router";
import { useClassicContext } from "~/data/classic_context";
import { isAndroid, isIOS } from "~/utils/platform";
import { UpiIdInputScreen } from "../upi_id_input/upi_id_input";
import PaymentStatusPollingScreen from "../payment_status_polling/payment_status_polling";
import { DonationDetails, getDonationDetails } from "~/data/donation";

function getNameForPaymentMode(transaction: SimulatedTransactionDetails) {
  switch (transaction.paymentMode) {
    case "CARD":
      return "Credit / Debit card";
    case "UPI":
      return "UPI";
  }
}
async function onPaymentComplete({
  orderId,
  cart,
  transactionId,
}: {
  orderId: string;
  cart: VoucherCart;
  transactionId: string;
}) {
  goBack();
  // wait for 300ms
  await new Promise((resolve) => setTimeout(resolve, 300));
  pushPage(
    () => (
      <VoucherGenLoader
        onVoucherGenerated={async (order, donationDetails) => {
          if (order.status == "COMPLETED") {
            await popUntilHome();
            sdkEventManager.sendEvent(HubbleEvent.PAYMENT_SUCCESS, {
              brandName: cart.voucherProduct?.name,
              amount: order.amount,
              vouchersCount: order.orderProductDetails?.vouchers?.length,
            });

            pushPage(
              () => (
                <VoucherGenSuccess
                  orderId={order.id}
                  orderDetails={order}
                  onClose={() => goBack()}
                  refreshProfile={true}
                  donationDetails={donationDetails}
                />
              ),
              {},
              HubbleRoute.VOUCHER_GENERATION_SUCCESS
            );
          }

          if (order.status == "FAILED") {
            sdkEventManager.sendEvent(HubbleEvent.PAYMENT_FAIL, {
              brandName: cart.voucherProduct?.name,
              amount: order.amount,
              vouchersCount: order.orderProductDetails?.vouchers?.length,
            });

            goBack(() => {
              pushPage(
                () => <VoucherGenerationFailed />,
                {},
                HubbleRoute.VOUCHER_GENERATION_FAIL
              );
            });
          }
        }}
        orderId={orderId}
        transactionId={transactionId}
      />
    ),
    {},
    HubbleRoute.VOUCHER_GENERATION_LOADER
  );
}

function launchUpiCollectFlow({
  redemptionResponse,
  cart,
}: {
  redemptionResponse: RedemptionResponse;
  cart: VoucherCart;
}) {
  pushPage(
    () =>
      UpiIdInputScreen({
        amount: redemptionResponse.amount,
        orderId: redemptionResponse.depositPaymentDetails.id,

        onCollectRequestSent: () => {
          pushPage(
            () => (
              <PaymentStatusPollingScreen
                orderId={redemptionResponse.depositPaymentDetails.id}
                onPaymentComplete={() => {
                  goBack();
                  onPaymentComplete({
                    orderId: redemptionResponse.storeOrderDetails.id,
                    cart: cart,
                    transactionId: redemptionResponse.id,
                  });
                }}
                onPaymentFailed={(failureReason: string) => goBack()}
              />
            ),
            { isModal: false },
            HubbleRoute.PAYMENT_STATUS_POLLING
          );
        },
      }),
    { isModal: false },
    HubbleRoute.UPI_ID_INPUT
  );
}

function launcUpiIntentFlow({
  redemptionResponse,
  initRedemptionJob,
  cart,
}: {
  redemptionResponse: RedemptionResponse;
  initRedemptionJob: JobWrapper<{
    res: RedemptionResponse;
    transaction: SimulatedTransactionDetails;
  }>;
  cart: VoucherCart;
}) {
  pushPage(
    () => (
      <PaymentLoader
        amount={redemptionResponse.depositPaymentDetails.amount}
        onPaymentComplete={() => {
          onPaymentComplete({
            orderId: redemptionResponse.storeOrderDetails.id,
            cart: cart,
            transactionId: redemptionResponse.id,
          });
        }}
        onPaymentFailed={(failureReason: string) =>
          console.log(`Payment failed`, failureReason)
        }
        orderId={redemptionResponse.depositPaymentDetails.id}
        brandImageUrl={
          cart.voucherProduct?.voucherProductMetadata.iconImageUrl ?? ""
        }
        reInit={() => {
          initRedemptionJob.run();
        }}
        onClose={goBack}
      />
    ),
    {
      isModal: true,
      disableCloseButton: true,
      preventClose: true,
      enterAnimation: false,
    },
    HubbleRoute.PAYMENT_LOADER
  );
}

async function launchRazorpay({
  redemptionResponse,
  paymentMode,
  email,
  phoneNumber,
  cart,
}: {
  redemptionResponse: RedemptionResponse;
  paymentMode: "CARD" | "UPI";
  email: string;
  phoneNumber: string;
  cart: VoucherCart;
}) {
  const order = await snackApiCallWrapper(
    payOrderStandardCheckout(redemptionResponse.depositPaymentDetails.id)
  );

  openRazorpayCheckout({
    amount: redemptionResponse.amount,
    email: email,
    mode: paymentMode,
    orderId: order.providerOrderId,
    phone: phoneNumber,
    onSuccess: () => {
      onPaymentComplete({
        orderId: redemptionResponse.storeOrderDetails.id,
        cart: cart,
        transactionId: redemptionResponse.id,
      });
    },
  });
}

export function PaymentOptions(props: { cart: VoucherCart }) {
  const { data, isClientDonationType } = useClassicContext();

  const v4Home = data.isLoggedIn ? data.v4HomePage : null;

  const [currentTransaction, setCurrentTransaction] =
    createSignal<SimulatedTransactionDetails>(
      props.cart.dryRunTransaction!.find((r) => r.paymentMode === "UPI")!
    );
  const [queryParams, setQueryParams] = useSearchParams();

  const initRedemptionJob = createJob({
    errorCallback: (e) => {
      showSnackBar({ level: "error", message: e.message });
    },
    successCallback: async ({ res, transaction }) => {
      if (transaction.paymentMode == "UPI") {
        if (
          config.isRazorpayEnabled ||
          // @ts-ignore
          (queryParams.clientId?.startsWith(config.clientIdPrefix.onepercent) &&
            isAndroid())
        ) {
          launchRazorpay({
            email: v4Home?.user.email ?? "",
            phoneNumber: v4Home?.user.phoneNumber ?? "",
            redemptionResponse: res,
            paymentMode: "UPI",
            cart: props.cart,
          });
        } else if (
          // @ts-ignore
          queryParams.clientId?.startsWith(config.clientIdPrefix.onepercent) &&
          isIOS()
        ) {
          launchUpiCollectFlow({
            redemptionResponse: res,
            cart: props.cart,
          });
        } else {
          launcUpiIntentFlow({
            redemptionResponse: res,
            initRedemptionJob,
            cart: props.cart,
          });
        }
      } else {
        launchRazorpay({
          email: v4Home?.user.email ?? "",
          phoneNumber: v4Home?.user.phoneNumber ?? "",
          redemptionResponse: res,
          paymentMode: "CARD",
          cart: props.cart,
        });
      }
    },
    initialJob: async () => {
      const transaction = currentTransaction();

      const res = await snackApiCallWrapper(
        initPgRedemption({
          amount: transaction.redemptionAmount,
          paymentMode: transaction.paymentMode,
          productId: props.cart.voucherProductId,
          denominationDetails: props.cart.denominations?.map((d) => {
            return {
              denomination: d.denomination,
              quantity: d.quantity,
            };
          }),
          variantId: props.cart.variantId,
          consumeCoins: props.cart.useCoins,
          couponCode: props.cart.couponCode,
        })
      );

      sdkEventManager.sendEvent(HubbleEvent.PAYMENT_INITIATED, {
        brandName: props.cart.voucherProduct?.name,
        amount: res.amount,
        paymentMode: transaction.paymentMode,
        consumeCoins: props.cart.useCoins,
        couponCode: props.cart.couponCode,
        productId: props.cart.voucherProductId,
      });

      return { res, transaction };
    },
  });

  return (
    <div
      class={`no-scrollbar absolute bottom-0 left-0 right-0 top-0 flex flex-col overflow-y-scroll bg-baseTertiaryMedium pl-5 pr-5 pt-[65px]`}
    >
      <Show when={initRedemptionJob.jobState() == "running"}>
        <div class="absolute bottom-0 left-0 right-0 top-0 flex flex-col items-center justify-center">
          <ThreeDotLoader color="#000" />
        </div>
      </Show>
      <BackButton />

      <span class="title-header text-h3 text-textDark">Complete payment</span>
      <span class="body-small text-medium text-textNormal">
        Item: {props.cart.voucherProduct?.name} card worth{" "}
        {toRupees(props.cart.dryRunTransaction![0].redemptionAmount)}{" "}
      </span>

      <div class="flex flex-col items-stretch gap-2 pt-4 sm:inline-flex sm:flex-row">
        <For
          each={
            props.cart.dryRunTransaction?.filter(
              (t) => t.paymentRoute != "WALLET"
            ) || []
          }
        >
          {(transaction) => {
            return (
              <OptionCardContent
                name={getNameForPaymentMode(transaction)}
                totalAmount={transaction.redemptionAmount}
                payableAmount={transaction.dueAmount}
                discountDetails={transaction.discountDetails}
                mode={transaction.paymentMode}
                isDonationFlow={isClientDonationType()}
                onClick={async () => {
                  setCurrentTransaction(transaction);
                  initRedemptionJob.run();
                }}
              />
            );
          }}
        </For>
      </div>
    </div>
  );
}

export function VoucherGenLoader(props: {
  orderId: string;
  transactionId: string;
  onVoucherGenerated: (
    order: OrderDetails,
    donationDetails?: DonationDetails
  ) => void;
}) {
  const { isClientDonationType } = useClassicContext();

  const getOrderDetail = async () => {
    const orderDetail = await getStoreOrderDetails(props.orderId);

    if (isClientDonationType() && orderDetail.status == "COMPLETED") {
      const donationDetails = await fetchDonationDetails();
      if (donationDetails) {
        return {
          orderDetail,
          donationDetails,
        };
      }
    }
    return {
      orderDetail,
    };
  };

  const fetchDonationDetails = async () => {
    for (let i = 0; i < 3; i++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const donationDetails = await getDonationDetails(props.transactionId);
        if (donationDetails) {
          return donationDetails;
        }
      } catch (e) {}
    }
  };

  const [order, manageOrder] = createResource(() => {
    return getOrderDetail();
  }, {});

  const interval = setInterval(() => {
    if (!order.loading) {
      manageOrder.refetch();
    }
  }, 5000);

  onCleanup(() => {
    clearInterval(interval);
  });

  createEffect(() => {
    if (!order()) return;
    if (order.loading) return;
    if (order.error) return;

    if (
      order()?.orderDetail.status != "INITIALISED" &&
      order()?.orderDetail.status != null &&
      order()?.orderDetail.status != "PROCESSING"
    ) {
      clearInterval(interval);
      props.onVoucherGenerated(order()?.orderDetail!, order()?.donationDetails);
    }
  });

  return (
    <div
      class="absolute bottom-0 left-0 right-0 top-0 flex flex-col items-center justify-center gap-4 bg-white p-5
"
    >
      <span class="title-section overflow-hidden  text-ellipsis text-h5 text-basePrimaryDark">
        Generating voucher
      </span>
      <ThreeDotLoader color="#000" />
    </div>
  );
}

export function VoucherGenerationFailed() {
  return (
    <div class="flex h-full flex-col items-center justify-center gap-1.5">
      <span class="overflow-hidden text-ellipsis text-h5 text-basePrimaryDark">
        Voucher generation failed
      </span>
      <span class="tetx-medium text-textNormal">
        Funds deducted will be refunded, please try again
      </span>

      <ButtonRegular
        onClick={async () => {
          await popUntilHome();
          pushPage(
            () => <RedemptionTransactions />,
            {},
            HubbleRoute.TRANSACTIONS
          );
        }}
        isEnabled={true}
      >
        Close
      </ButtonRegular>
    </div>
  );
}

function OptionCardContent(props: {
  totalAmount: number;
  payableAmount: number;
  discountDetails: DiscountDetails;
  name: string;
  mode: "UPI" | "CARD";
  onClick: () => void;
  isDonationFlow: boolean;
}) {
  const savings = props.discountDetails.totalDiscount.amount;

  return (
    <div
      class={classNames(
        "",
        "flex cursor-pointer flex-col items-stretch gap-4 self-stretch rounded-2xl border border-baseTertiaryDark bg-baseTertiaryLight p-4 sm:min-w-[250px]"
      )}
      onClick={props.onClick}
    >
      <div class="flex flex-row justify-between">
        <span class="caption-overline text-f12Bold uppercase text-textNormal">
          {props.name}
        </span>
        <span class="input-label text-f12Bold text-successDark">
          {props.isDonationFlow
            ? `Donating ${toRupees(props.discountDetails.hubbleBaseDiscount.amount)}`
            : `Saving ${toRupees(savings)}`}
        </span>
      </div>
      <span class="title-section text-h5 text-textDark">
        You'll pay: {toRupees(props.payableAmount)}{" "}
        <span class="body-small text-medium text-textNormal line-through">
          {toRupees(props.totalAmount)}
        </span>
      </span>
      <div class="border-t border-baseTertiaryMedium pt-4">
        <Switch>
          <Match when={props.mode == "CARD"}>
            <div class="flex flex-row items-center gap-3">
              <PhosphorIcon
                name="credit-card"
                class="rounded-[18px] border border-baseTertiaryMedium bg-baseTertiaryLight p-1.5"
              />
              <div class="flex flex-[1] flex-col items-start gap-2">
                <span class="body-normal">Pay via credit/debit card</span>
                <img
                  src={assetCardIcon}
                  class="h-[22px]"
                  alt="Credit card providers"
                />
              </div>
              <PhosphorIcon
                name="caret-right"
                size="bold"
                class="text-[22px]"
              />
            </div>
          </Match>

          <Match when={props.mode == "UPI"}>
            <div class="flex flex-row items-center gap-3">
              <img
                src={upiLogo}
                class="h-[36px] rounded-[40px] border border-baseTertiaryMedium"
                alt="Upi"
              />
              <div class="flex flex-[1] flex-col items-start gap-2">
                <span class="body-normal">Pay using UPI apps</span>
                <img src={assetUpiApps} class="h-[22px]" alt="Upi apps" />
              </div>
              <PhosphorIcon
                name="caret-right"
                size="bold"
                class="text-[22px]"
              />
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  );
}

function BackButton() {
  return (
    <PhosphorIcon
      name="arrow-left"
      class="absolute left-4 top-4 cursor-pointer rounded-[24px] border border-baseTertiaryDark bg-[white] p-[6px]"
      onClick={() => goBack()}
    />
  );
}
